import ApiClient from "../apiClient";

export interface ProxVoiceSocialLinks {
  x?: string;
  substack?: string;
  telegram?: string;
  discord?: string;
}

export interface ProxVoicePublicConnection {
  id: string;
  providerId: string;
  label: string;
  handle?: string;
  url: string;
  isPublic: boolean;
}

export type ProxVoiceAvatarColor =
  "orange" | "goldenrod" | "teal" | "light-green" | "purple";

export interface ProxVoiceProfile {
  userId: string;
  displayName: string;
  avatarUrl: string | null;
  avatarColor: ProxVoiceAvatarColor;
  bio: string | null;
  position: string | null;
  broker: string | null;
  socialLinks: ProxVoiceSocialLinks;
  publicConnections: ProxVoicePublicConnection[];
}

export interface ProxVoicePresence {
  userId: string;
  deskId: string | null;
  deskSlug: string | null;
  surface: string;
  ticker: string | null;
  sessionId: string | null;
  muted: boolean;
  deafened: boolean;
  updatedAt: string;
  profile: ProxVoiceProfile;
}

export class ProxVoiceService {
  constructor(private client: ApiClient) {}

  async token(): Promise<{ token: string; url: string; roomName: string }> {
    return this.client.post("/api/proxvoice/token", {});
  }

  async participants(
    deskId?: string | null,
  ): Promise<{ participants: ProxVoicePresence[] }> {
    const query = deskId ? `?deskId=${encodeURIComponent(deskId)}` : "";
    return this.client.get(`/api/proxvoice/participants${query}`);
  }

  async presence(data: {
    surface?: string;
    deskId?: string | null;
    ticker?: string | null;
    sessionId?: string | null;
    muted?: boolean;
    deafened?: boolean;
  }): Promise<{ presence: ProxVoicePresence }> {
    return this.client.post("/api/proxvoice/presence", data);
  }

  async leavePresence(): Promise<{ ok: boolean }> {
    return this.client.delete("/api/proxvoice/presence");
  }

  async getSocialLinks(): Promise<{ socialLinks: ProxVoiceSocialLinks }> {
    return this.client.get("/api/profile/social-links");
  }

  async updateSocialLinks(
    socialLinks: ProxVoiceSocialLinks,
  ): Promise<{ socialLinks: ProxVoiceSocialLinks }> {
    return this.client.put("/api/profile/social-links", { socialLinks });
  }

  async updatePublicConnections(
    publicConnections: ProxVoicePublicConnection[],
  ): Promise<{ publicConnections: ProxVoicePublicConnection[] }> {
    return this.client.put("/api/profile/public-connections", {
      publicConnections,
    });
  }

  async getProfile(): Promise<{ profile: ProxVoiceProfile }> {
    return this.client.get("/api/profile/voice-profile");
  }

  async updateProfile(data: {
    displayName?: string;
    avatarUrl?: string | null;
    avatarColor?: ProxVoiceAvatarColor;
    bio?: string | null;
    position?: string | null;
    broker?: string | null;
    socialLinks?: ProxVoiceSocialLinks;
    publicConnections?: ProxVoicePublicConnection[];
  }): Promise<{ profile: ProxVoiceProfile }> {
    return this.client.put("/api/profile/voice-profile", data);
  }
}
